import { db, storage } from "./config"
import { collection, addDoc, getDocs, query, where, doc, updateDoc, getDoc, deleteDoc, runTransaction, serverTimestamp } from "firebase/firestore"
import { getDownloadURL, ref, uploadBytes } from "firebase/storage"
import { getShippingRecordBySaleId } from "./shipping"
import { addNotification } from "./notifications"
import { sendEmailNotificationWithRetry } from "./emailNotifications"
import { getSellerIdsForUser } from "./shopify"
import { getDocsWhereIn } from "./queries"

function groupSalesByCardId(sales) {
        const grouped = {};

        sales.forEach((sale) => {
            const { cardId, count, ...rest } = sale;
            if (!grouped[cardId]) {
                grouped[cardId] = { cardId, count: 0, ...rest };
            }
            grouped[cardId].count += count;
        });

        return Object.values(grouped);
    }

export const getPurchasedListings = async (userId) => {
    try {
        // Reference to the "sales" collection
        const salesRef = collection(db, "sales")
        const sales = []
        // Create a query to filter sales by buyerId
        const q = query(salesRef, where("sender", "==", userId))
        const querySnapshot = await getDocs(q)

        querySnapshot.forEach((doc) => {
            sales.push({ id: doc.id, ...doc.data() })
        })
        
        // Add shipping information to each individual sale
        for (let sale of sales) {
            const shippingRecord = await getShippingRecordBySaleId(sale.id)
            sale.shipping = shippingRecord
        }
        
        sales.sort((a, b) => b.created_at - a.created_at)
        return sales // Return individual sales records
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const getSoldListings = async (userId) => {
    try {
        // Reference to the "sales" collection
        const salesRef = collection(db, "sales")

        const sales = []

        // A Shopify sale's receiver is the store's role id, not a user id, so
        // "my sales" means this account plus every store it owns. Whoever owns
        // a store owns its whole order history, which is what makes handing a
        // store over a single write instead of a rewrite of every order.
        const sellerIds = await getSellerIdsForUser(userId)
        const docs = await getDocsWhereIn(salesRef, "receiver", sellerIds)

        docs.forEach((doc) => {
            sales.push({ id: doc.id, ...doc.data() })
        })
        
        // Add shipping information to each individual sale
        for (let sale of sales) {
            const shippingRecord = await getShippingRecordBySaleId(sale.id)
            sale.shipping = shippingRecord
        }
        
        sales.sort((a, b) => b.created_at - a.created_at);
        return sales // Return individual sales records
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const addReview = async ({ saleId, title, description, rating, reviewer }) => {
    try {
        const salesRef = doc(db, 'sales', saleId);

        // Update the document with the new review
        await updateDoc(salesRef, {
            review: {
                title,
                description,
                rating,
                reviewer_name: reviewer.displayName,
                created_at: new Date().toISOString() // Optional: add a timestamp
            }
        });

        console.log('Review added successfully');
    } catch (error) {
        console.error('Error adding review: ', error);
    }
};

// Get individual sale record with shipping information
export const getSaleRecord = async (saleId) => {
    try {
        const saleRef = doc(db, 'sales', saleId);
        const saleDoc = await getDoc(saleRef);
        
        if (saleDoc.exists()) {
            const saleData = { id: saleDoc.id, ...saleDoc.data() };
            
            // Add shipping information
            const shippingRecord = await getShippingRecordBySaleId(saleId);
            saleData.shipping = shippingRecord;
            
            return saleData;
        }
        
        return null;
    } catch (error) {
        console.error('Error getting sale record: ', error);
        return null;
    }
};

// Debug function to see actual data structure
export const debugSalesData = async (userId) => {
    try {
        const salesRef = collection(db, "sales");
        const q = query(salesRef, where("sender", "==", userId));
        const querySnapshot = await getDocs(q);
        
        const sales = [];
        querySnapshot.forEach((doc) => {
            sales.push({ id: doc.id, ...doc.data() });
        });
        
        console.log('Debug - Raw sales data:', sales);
        console.log('Debug - Sales count:', sales.length);
        
        if (sales.length > 0) {
            console.log('Debug - First sale structure:', sales[0]);
        }
        
        return sales;
    } catch (error) {
        console.error('Error debugging sales data:', error);
        return [];
    }
};

// Create a sale with notifications
export const createSaleWithNotifications = async (saleData) => {
    try {
        console.log('Creating sale with notifications:', saleData);
        
        // Create the sale
        const salesColRef = collection(db, "sales");
        const counterRef = doc(db, "counters", "sales"); // global counter: { next: 1 }
        const newSaleRef = doc(salesColRef);             // pre-generate doc id for notifications

        // Transaction: atomically read/increment counter and create the sale
        await runTransaction(db, async (tx) => {
        const snap = await tx.get(counterRef);

        let nextNumber = 1;
        if (!snap.exists()) {
            // initialize counter and assign number 1 in the same transaction
            tx.set(counterRef, { next: 2 });
            nextNumber = 1;
        } else {
            const current = snap.data()?.next ?? 1;
            nextNumber = current;
            tx.update(counterRef, { next: current + 1 });
        }

        tx.set(newSaleRef, {
            ...saleData,
            number: nextNumber,          // sequential, collision-safe
            created_at: serverTimestamp()
        });
        });
        
        console.log('Sale created with ID:', newSaleRef.id);
        
        // Send notification to seller
        try {
            const sellerNotificationData = {
                type: 'purchase_completed',
                action: 'purchased',
                listingId: saleData.cardId,
                listingTitle: saleData.title,
                recipientId: saleData.receiver, // Notify the seller
                message: `Your listing "${saleData.title}" has been purchased for $${saleData.price.toFixed(2)}.`,
                timestamp: new Date(),
                isRead: false
            };
            
            await addNotification(sellerNotificationData);
            console.log('Purchase notification sent to seller:', saleData.receiver);
        } catch (notificationError) {
            console.error('Error sending purchase notification to seller:', notificationError);
        }

        // Send notification to buyer
        try {
            const buyerNotificationData = {
                type: 'purchase_completed',
                action: 'purchased',
                listingId: saleData.cardId,
                listingTitle: saleData.title,
                recipientId: saleData.sender, // Notify the buyer
                message: `You have successfully purchased "${saleData.title}" for $${saleData.price.toFixed(2)}.`,
                timestamp: new Date(),
                isRead: false
            };
            
            await addNotification(buyerNotificationData);
            console.log('Purchase notification sent to buyer:', saleData.sender);
        } catch (notificationError) {
            console.error('Error sending purchase notification to buyer:', notificationError);
        }
        
        return newSaleRef.id;
    } catch (error) {
        console.error('Error creating sale with notifications:', error);
        throw error;
    }
};

// Add notifications to existing sales (for retroactive notifications)
export const addNotificationsToExistingSales = async () => {
    try {
        console.log('Adding notifications to existing sales...');
        
        const salesRef = collection(db, "sales");
        const querySnapshot = await getDocs(salesRef);
        
        let count = 0;
        for (const doc of querySnapshot.docs) {
            const saleData = { id: doc.id, ...doc.data() };
            
            // Skip if already has notifications
            if (saleData.notifications_sent) continue;
            
            try {
                console.log('Processing sale:', saleData);
                
                // Send notification to seller
                const sellerNotificationData = {
                    type: 'purchase_completed',
                    action: 'purchased',
                    listingId: saleData.cardId,
                    listingTitle: saleData.listing_title || saleData.title,
                    recipientId: saleData.receiver,
                    message: `Your listing "${saleData.listing_title || saleData.title}" has been purchased for $${saleData.price.toFixed(2)}.`,
                    timestamp: new Date(),
                    isRead: false
                };
                
                console.log('Sending seller notification:', sellerNotificationData);
                await addNotification(sellerNotificationData);
                
                // Send notification to buyer
                const buyerNotificationData = {
                    type: 'purchase_completed',
                    action: 'purchased',
                    listingId: saleData.cardId,
                    listingTitle: saleData.listing_title || saleData.title,
                    recipientId: saleData.sender,
                    message: `You have successfully purchased "${saleData.listing_title || saleData.title}" for $${saleData.price.toFixed(2)}.`,
                    timestamp: new Date(),
                    isRead: false
                };
                
                console.log('Sending buyer notification:', buyerNotificationData);
                await addNotification(buyerNotificationData);
                
                // Mark as processed
                await updateDoc(doc.ref, { notifications_sent: true });
                count++;
                
                console.log(`✅ Added notifications for sale ${saleData.id}`);
            } catch (error) {
                console.error(`❌ Error adding notifications for sale ${saleData.id}:`, error);
            }
        }
        
        console.log(`✅ Added notifications to ${count} sales`);
        return count;
    } catch (error) {
        console.error('❌ Error adding notifications to existing sales:', error);
        throw error;
    }
};

// Simple function to add notification for a single sale (for testing)
export const addNotificationForSale = async (saleId) => {
    try {
        console.log('Adding notification for sale:', saleId);
        
        const saleRef = doc(db, "sales", saleId);
        const saleDoc = await getDoc(saleRef);
        
        if (!saleDoc.exists()) {
            console.error('Sale not found:', saleId);
            return false;
        }
        
        const saleData = saleDoc.data();
        console.log('Sale data:', saleData);
        
        // Send notification to seller
        const sellerNotificationData = {
            type: 'purchase_completed',
            action: 'purchased',
            listingId: saleData.cardId,
            listingTitle: saleData.listing_title || saleData.title,
            recipientId: saleData.receiver,
            message: `Your listing "${saleData.listing_title || saleData.title}" has been purchased for $${saleData.price.toFixed(2)}.`,
            timestamp: new Date(),
            isRead: false
        };
        
        console.log('Sending seller notification:', sellerNotificationData);
        await addNotification(sellerNotificationData);
        
        // Send notification to buyer
        const buyerNotificationData = {
            type: 'purchase_completed',
            action: 'purchased',
            listingId: saleData.cardId,
            listingTitle: saleData.listing_title || saleData.title,
            recipientId: saleData.sender,
            message: `You have successfully purchased "${saleData.listing_title || saleData.title}" for $${saleData.price.toFixed(2)}.`,
            timestamp: new Date(),
            isRead: false
        };
        
        console.log('Sending buyer notification:', buyerNotificationData);
        await addNotification(buyerNotificationData);
        
        console.log('✅ Notifications added for sale:', saleId);
        return true;
    } catch (error) {
        console.error('❌ Error adding notification for sale:', saleId, error);
        return false;
    }
};

// Function to add notifications immediately after successful checkout
export const addNotificationsForSuccessfulCheckout = async (saleData) => {
    try {
        console.log('=== ADDING NOTIFICATIONS FOR SUCCESSFUL CHECKOUT ===');
        console.log('Sale data received:', saleData);
        
        // Validate sale data
        if (!saleData.cardId || !saleData.receiver || !saleData.sender || !saleData.listing_title) {
            console.error('❌ Invalid sale data:', saleData);
            return false;
        }
        
        // Send notification to seller
        const sellerNotificationData = {
            type: 'purchase_completed',
            action: 'purchased',
            listingId: saleData.cardId,
            listingTitle: saleData.listing_title,
            recipientId: saleData.receiver,
            message: `Your listing "${saleData.listing_title}" has been purchased for $${saleData.price.toFixed(2)}.`,
            timestamp: new Date(),
            isRead: false
        };
        
        console.log('Sending seller notification:', sellerNotificationData);
        const sellerResult = await addNotification(sellerNotificationData);
        console.log('Seller notification result:', sellerResult);
        
        // Send notification to buyer
        const buyerNotificationData = {
            type: 'purchase_completed',
            action: 'purchased',
            listingId: saleData.cardId,
            listingTitle: saleData.listing_title,
            recipientId: saleData.sender,
            message: `You have successfully purchased "${saleData.listing_title}" for $${saleData.price.toFixed(2)}.`,
            timestamp: new Date(),
            isRead: false
        };
        
        console.log('Sending buyer notification:', buyerNotificationData);
        const buyerResult = await addNotification(buyerNotificationData);
        console.log('Buyer notification result:', buyerResult);
        
        console.log('✅ Notifications added for successful checkout');
        return true;
    } catch (error) {
        console.error('❌ Error adding notifications for successful checkout:', error);
        return false;
    }
};